import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef, useState } from 'react';
import { OPENX_AGENT_POSITION_STORAGE_KEY, OPENX_AGENT_SIZE_STORAGE_KEY } from '../../../context/consts';
import {
  readStoredPosition,
  readStoredSize,
  writeStoredPosition,
  writeStoredSize,
} from '../../../context/openXAgentStorage';
import {
  DEFAULT_HEIGHT,
  DEFAULT_MIN_HEIGHT,
  DEFAULT_MIN_WIDTH,
  DEFAULT_WIDTH,
  WINDOW_RESIZE_DEBOUNCE_MS,
} from './consts';
import {
  calculateDefaultPosition,
  clamp,
  computeDragPosition,
  computeResizeViewport,
  fitSizeToViewport,
  fitViewportToWindow,
  getViewportBounds,
} from './geometry';
import { rafThrottle } from './rafThrottle';
import type {
  DragStartRef,
  ModalPosition,
  ModalSize,
  ModalViewport,
  OpenXAgentDragEvent,
  OpenXAgentResizeEvent,
  ResizeDirection,
  ResizeStartRef,
} from './types';

type UseDraggableAndResizableParams = {
  initialPosition?: ModalPosition | undefined;
  initialSize?: ModalSize | undefined;
  minWidth?: number | undefined;
  minHeight?: number | undefined;
  maxWidth?: number | undefined;
  maxHeight?: number | undefined;
  positionStorageKey?: string | undefined;
  sizeStorageKey?: string | undefined;
  // Adjusts the viewport restored on mount, e.g. to make room for content that is already open.
  mapInitialViewport?: ((viewport: ModalViewport) => ModalViewport) | undefined;
  // Adjusts the viewport right before it is written to storage.
  mapPersistedViewport?: ((viewport: ModalViewport) => ModalViewport) | undefined;
};

const resolveInitialViewport = (
  initialPosition: ModalPosition | undefined,
  initialSize: ModalSize | undefined,
  positionStorageKey: string,
  sizeStorageKey: string
): ModalViewport => {
  const storedSize = readStoredSize(sizeStorageKey);
  const size = fitSizeToViewport(initialSize ?? storedSize ?? { width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });

  if (initialPosition) return { position: initialPosition, size };

  const storedPosition = readStoredPosition(positionStorageKey);
  if (storedPosition) {
    const { minX, maxX, minY, maxY } = getViewportBounds(size.width, size.height);

    return { position: { x: clamp(storedPosition.x, minX, maxX), y: clamp(storedPosition.y, minY, maxY) }, size };
  }

  return { position: calculateDefaultPosition(size.width, size.height), size };
};

export const useDraggableAndResizable = ({
  initialPosition,
  initialSize,
  minWidth = DEFAULT_MIN_WIDTH,
  minHeight = DEFAULT_MIN_HEIGHT,
  maxWidth,
  maxHeight,
  positionStorageKey = OPENX_AGENT_POSITION_STORAGE_KEY,
  sizeStorageKey = OPENX_AGENT_SIZE_STORAGE_KEY,
  mapInitialViewport,
  mapPersistedViewport,
}: UseDraggableAndResizableParams = {}) => {
  const [initialViewport] = useState(() => {
    const viewport = resolveInitialViewport(initialPosition, initialSize, positionStorageKey, sizeStorageKey);
    return mapInitialViewport ? mapInitialViewport(viewport) : viewport;
  });
  const [size, setSize] = useState<ModalSize>(initialViewport.size);
  const [position, setPosition] = useState<ModalPosition>(initialViewport.position);

  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const interactionStartRef = useRef<ModalViewport | null>(null);

  const positionRef = useRef(position);
  const sizeRef = useRef(size);
  useEffect(() => {
    positionRef.current = position;
  }, [position]);
  useEffect(() => {
    sizeRef.current = size;
  }, [size]);
  const mapPersistedViewportRef = useRef(mapPersistedViewport);
  useEffect(() => {
    mapPersistedViewportRef.current = mapPersistedViewport;
  }, [mapPersistedViewport]);

  const dragStartRef = useRef<DragStartRef>(null);

  const resizeStartRef = useRef<ResizeStartRef>(null);

  const handleDragStart = useCallback(
    (event: OpenXAgentDragEvent) => {
      if (event.button !== 0) return;

      const target = event.target as HTMLElement | null;
      if (target?.closest('button') || target?.closest('[role="button"]')) {
        return;
      }

      event.preventDefault();
      setIsDragging(true);
      dragStartRef.current = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        posX: position.x,
        posY: position.y,
      };
    },
    [position]
  );

  const handleResizeStart = useCallback(
    (direction: ResizeDirection, event: OpenXAgentResizeEvent) => {
      if (event.button !== 0) return;

      event.preventDefault();
      event.stopPropagation();
      setIsResizing(true);
      resizeStartRef.current = {
        mouseX: event.clientX,
        mouseY: event.clientY,
        width: size.width,
        height: size.height,
        posX: position.x,
        posY: position.y,
        direction,
      };
    },
    [position, size]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = rafThrottle((event: MouseEvent) => {
      if (!dragStartRef.current) return;

      setPosition(computeDragPosition(dragStartRef.current, event, size));
    });

    const handleMouseUp = () => {
      setIsDragging(false);
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      handleMouseMove.cancel();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, size]);

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = rafThrottle((event: MouseEvent) => {
      if (!resizeStartRef.current) return;

      const next = computeResizeViewport(resizeStartRef.current, event, { minWidth, minHeight, maxWidth, maxHeight });
      setSize(next.size);
      setPosition(next.position);
    });

    const handleMouseUp = () => {
      setIsResizing(false);
      resizeStartRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      handleMouseMove.cancel();
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, maxHeight, maxWidth, minHeight, minWidth]);

  // Re-fits the modal inside the viewport whenever the window is resized (or DevTools toggled),
  // so the modal never ends up partly off-screen with an unreachable bottom/right edge.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleWindowResize = debounce(() => {
      const fitted = fitViewportToWindow(positionRef.current, sizeRef.current);
      setSize(fitted.size);
      setPosition(fitted.position);
    }, WINDOW_RESIZE_DEBOUNCE_MS);

    window.addEventListener('resize', handleWindowResize);
    return () => {
      handleWindowResize.cancel();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  // Persist position and size once the current drag/resize interaction ends. Each is stored under
  // its own key and written only when the interaction changed it, so a drag never overwrites the stored size.
  useEffect(() => {
    if (isDragging || isResizing) {
      interactionStartRef.current ??= { position, size };
      return;
    }

    const start = interactionStartRef.current;
    if (!start) return;
    interactionStartRef.current = null;

    const viewport = { position, size };
    const persisted = mapPersistedViewportRef.current?.(viewport) ?? viewport;
    if (position !== start.position) writeStoredPosition(persisted.position, positionStorageKey);
    if (size !== start.size) writeStoredSize(persisted.size, sizeStorageKey);
  }, [isDragging, isResizing, position, size, positionStorageKey, sizeStorageKey]);

  return {
    position,
    size,
    isDragging,
    isResizing,
    handleDragStart,
    handleResizeStart,
    setPosition,
    setSize,
  };
};
